import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { mesesRecentes } from '@/metas/calculo';
import { consolidarFechamento, escolherExibicao } from '@/remuneracao/calculo';
import {
  PASSO_A_PASSO,
  escolherMesInicial,
  lancamentosDoMes,
  mesesComComparecimento,
  mesesCandidatos,
  podeOferecerFechar,
  textoDaConfirmacao,
  ultimaImportacaoConfirmada,
} from '@/remuneracao/folhaDaTela';
import { ouvirAgendamentos } from '@/services/agendamentosService';
import { buscarImportacoes } from '@/services/importacaoService';
import {
  buscarMesesFechados,
  buscarPessoasDoFechamento,
  buscarRecebimentosDoMes,
  fecharMes,
  haRecebimentoNoMes,
  ouvirConfigs,
  ouvirFechamento,
  reabrirMes,
} from '@/services/remuneracaoService';
import { ouvirUsuariosDaClinica } from '@/services/usuariosService';

/**
 * A folha do mês (VC) — só o gestor.
 *
 * Mês ABERTO mostra o cálculo vivo, marcado como **prévia**. Mês FECHADO mostra
 * o SNAPSHOT, e não recalcula: se um recebimento for corrigido depois, o que já
 * foi pago não pode mudar. É a razão de a peça existir.
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** CSV para a folha — Excel brasileiro espera `;`, e o BOM traz os acentos. */
function exportarCsv(linhas, mes) {
  const cabecalho = ['Pessoa', 'Modelo', 'Comparecimentos', 'Valor fixo', 'Recebido no mês', 'Percentual', 'Comissão', 'Total a pagar'];
  const escapar = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  // Números em formato brasileiro: quem abre isto vai somar na planilha.
  const num = (v) => String(Number(v ?? 0).toFixed(2)).replace('.', ',');
  const corpo = linhas.map((l) =>
    [l.nome, l.modelo ?? '', l.comparecimentos, num(l.valorFixo), num(l.baseRecebida), num(l.percentual), num(l.comissao), num(l.total)]
      .map(escapar).join(';'));
  const csv = '﻿' + [cabecalho.map(escapar).join(';'), ...corpo].join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `remuneracao-${mes}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function RemuneracaoPage() {
  const { clinicaId } = useAuth();
  const { leads, carregando: carregandoLeads } = useLeads();

  /**
   * ★★ Travas do fechamento (03/11/2026, item 3) — a tela NÃO abre mais no mês corrente (que não
   * pode ser fechado): abre no mês aberto mais antigo com movimento, ou no anterior ao atual.
   * `null` até decidir.
   */
  const [mes, setMes] = useState(null);
  /**
   * ★★ Item 5 — a MESMA equipe do servidor: ativos E desativados. ⚠ O `useLeads().equipe` é só
   * `ativo == true`; com ele, o comparecimento agendado por quem saiu caía no `sdrId` na prévia e
   * ia para quem agendou no fechamento — a prévia e o pago divergiam (caso [i], 03/11/2026).
   */
  const [equipe, setEquipe] = useState(null);
  const [ultimaImportacao, setUltimaImportacao] = useState(null);
  const [agendamentos, setAgendamentos] = useState([]);
  const [recebimentos, setRecebimentos] = useState(null);
  const [configs, setConfigs] = useState(null);
  const [fechamento, setFechamento] = useState(undefined);
  const [snapshot, setSnapshot] = useState(null);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirConfigs(clinicaId, setConfigs, () => setErro('Não foi possível carregar as configurações.'));
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirUsuariosDaClinica(clinicaId, setEquipe, () => setErro('Não foi possível carregar a equipe.'));
  }, [clinicaId]);

  // ★ Item 7 — a data da última importação confirmada, para a confirmação do fechamento.
  useEffect(() => {
    if (!clinicaId) return;
    buscarImportacoes(clinicaId)
      .then((lista) => setUltimaImportacao(ultimaImportacaoConfirmada(lista)))
      .catch(() => setUltimaImportacao(null));
  }, [clinicaId]);

  // ★★ Item 3 — em que mês abrir. Uma vez, quando os leads chegam.
  useEffect(() => {
    if (!clinicaId || mes || carregandoLeads) return;
    let vivo = true;
    (async () => {
      const agora = new Date();
      try {
        const fechados = await buscarMesesFechados(clinicaId);
        const comComparecimento = mesesComComparecimento(leads);
        const comRecebimento = new Set();
        // Só os meses abertos, do mais antigo: a primeira resposta positiva já decide.
        for (const m of mesesCandidatos(agora)) {
          if (fechados.has(m) || comComparecimento.has(m)) continue;
          if (await haRecebimentoNoMes(clinicaId, m)) { comRecebimento.add(m); break; }
        }
        const inicial = escolherMesInicial({
          agora,
          fechados,
          temMovimento: (m) => comComparecimento.has(m) || comRecebimento.has(m),
        });
        if (vivo) setMes(inicial);
      } catch (e) {
        console.error('[OdontoSDR] Falha ao escolher o mês da folha:', e);
        if (vivo) setMes(escolherMesInicial({ agora }));
      }
    })();
    return () => { vivo = false; };
  }, [clinicaId, mes, carregandoLeads, leads]);

  useEffect(() => {
    if (!clinicaId || !mes) return undefined;
    setFechamento(undefined);
    return ouvirFechamento(clinicaId, mes, setFechamento, () => setErro('Não foi possível ler o fechamento.'));
  }, [clinicaId, mes]);

  useEffect(() => {
    if (!clinicaId || !mes) return undefined;
    const [ano, m] = mes.split('-').map(Number);
    return ouvirAgendamentos(
      clinicaId,
      { de: new Date(ano, m - 1, 1), ate: new Date(ano, m, 0, 23, 59, 59) },
      setAgendamentos,
      () => setErro('Não foi possível carregar os agendamentos.'),
    );
  }, [clinicaId, mes]);

  useEffect(() => {
    if (!clinicaId || !mes) return;
    setRecebimentos(null);
    buscarRecebimentosDoMes(clinicaId, mes)
      .then(setRecebimentos)
      .catch((e) => {
        console.error('[OdontoSDR] Falha ao buscar recebimentos do mês:', e);
        setErro('Não foi possível carregar os recebimentos do mês.');
      });
  }, [clinicaId, mes]);

  // ⚠ A ORDEM AQUI NÃO É ESTILO. `previa` é `const`: consumi-la antes desta
  // linha é zona morta temporal, e o componente inteiro cai no primeiro render
  // com `Cannot access 'previa' before initialization` — a tela abre em branco,
  // sem nem o menu. Foi exatamente o que foi para produção em 07/set.
  const previa = useMemo(() => {
    if (!mes || !configs || !recebimentos || !equipe) return null;
    return consolidarFechamento({ leads, agendamentos, recebimentos, configs, equipe, mes });
  }, [leads, agendamentos, recebimentos, configs, equipe, mes]);

  const { fechado } = escolherExibicao(fechamento, previa);

  useEffect(() => {
    if (!clinicaId || !fechado) { setSnapshot(null); return; }
    buscarPessoasDoFechamento(clinicaId, mes)
      .then(setSnapshot)
      .catch(() => setErro('Não foi possível ler o fechamento gravado.'));
  }, [clinicaId, mes, fechado]);

  if (carregandoLeads || !mes || !previa || fechamento === undefined) return <Carregando />;

  // ★ Item 5 — quem saiu da clínica continua na folha (o servidor paga), marcado.
  const desativados = new Set((equipe ?? []).filter((u) => u.ativo === false).map((u) => u.id));
  const agora = new Date();

  // ★ Mês fechado NÃO recalcula: as linhas vêm do snapshot.
  const linhas = fechado && snapshot ? [...snapshot].sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR')) : previa.linhas;
  const totais = fechado && fechamento?.totais ? fechamento.totais : previa.totais;
  const oferta = podeOferecerFechar({ mes, agora, fechado, totais });

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Remuneração — {mes}</h1>
        <div className="linha">
          <select value={mes} onChange={(e) => setMes(e.target.value)}>
            {[...new Set([...mesesRecentes(), mes])].sort().reverse().map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {/* ★ O ÚNICO caminho para `/remuneracao/config`, e por isso fica no
              CABEÇALHO e não no rodapé: quando ninguém tem taxa configurada a
              tabela vem vazia, e um botão preso à tabela sumiria justamente na
              hora em que ele é necessário.
              Não virou item de menu porque Vendas já tem doze, e configurar taxa
              é ato raro — uma vez por pessoa, quando se combina —, enquanto a
              folha é mensal. O caminho natural é ver o número e ir ajustá-lo. */}
          <Link to="/remuneracao/config" className="btn-secundario">
            Configurar remuneração
          </Link>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      {/* ★★ Item 7 — o passo a passo, curto, sempre à vista. */}
      <ol className="campo-ajuda" aria-label="Como fechar o mês">
        {PASSO_A_PASSO.map((passo) => <li key={passo}>{passo}</li>)}
      </ol>

      {fechado ? (
        <p className="aviso">
          <strong>Mês fechado</strong> em{' '}
          {fechamento.fechadoEm?.toDate ? format(fechamento.fechadoEm.toDate(), 'dd/MM/yyyy HH:mm') : '—'} por{' '}
          {fechamento.fechadoPorNome ?? '—'}. Os valores abaixo são os que foram{' '}
          <strong>pagos</strong> — não mudam se um recebimento for corrigido agora.
        </p>
      ) : (
        <p className="secao-sub">
          <strong>Prévia</strong> — o mês ainda não foi fechado, e estes números mudam
          conforme entram comparecimentos e recebimentos.
        </p>
      )}

      {/* O comparecimento aqui NÃO é o mesmo número da tela de Metas. */}
      <p className="campo-ajuda">
        Comparecimento conta <strong>uma vez por paciente</strong>, no mês da primeira
        consulta. Na tela de Metas o número é por consulta, então um paciente que voltou
        aparece duas vezes lá e uma vez aqui — os dois estão certos, medem coisas
        diferentes.
      </p>

      <div className="tabela-envolve">
        <table className="tabela">
          <thead>
            <tr>
              <th>Pessoa</th><th>Comparecimentos</th><th>Fixo</th>
              <th>Recebido no mês</th><th>%</th><th>Comissão</th><th>Total a pagar</th>
            </tr>
          </thead>
          <tbody>
            {linhas.length === 0 ? (
              <tr>
                <td colSpan={7} className="em-breve">
                  Ninguém com remuneração configurada e movimento no mês. Use{' '}
                  <Link to="/remuneracao/config">Configurar remuneração</Link> para definir
                  o modelo e as taxas de cada pessoa — quem não tiver configuração não
                  aparece aqui.
                </td>
              </tr>
            ) : (
              linhas.map((l) => (
                <tr key={l.id || 'sem-responsavel'} className={l.id ? undefined : 'linha-apagada'}>
                  <td>
                    <div>
                      {l.nome}
                      {l.id && desativados.has(l.id) && <span className="campo-ajuda"> · desativada</span>}
                    </div>
                    {!l.id && (
                      <div className="campo-ajuda">
                        não paga a ninguém; está aqui para os totais fecharem
                      </div>
                    )}
                  </td>
                  <td>{l.comparecimentos}</td>
                  <td>{moeda(l.valorFixo)}</td>
                  <td>{moeda(l.baseRecebida)}</td>
                  <td>{l.percentual ? `${String(l.percentual).replace('.', ',')}%` : '—'}</td>
                  <td>{moeda(l.comissao)}</td>
                  <td className="valor">{moeda(l.total)}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              <th>{totais.comparecimentos}</th>
              <th>{moeda(totais.valorFixo)}</th>
              <th>{moeda(totais.baseRecebida)}</th>
              <th>—</th>
              <th>{moeda(totais.comissao)}</th>
              <th>{moeda(totais.total)}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <footer className="dialogo-acoes">
        <button type="button" className="btn-secundario" onClick={() => exportarCsv(linhas, mes)}>
          Exportar CSV
        </button>
        {fechado ? (
          <button
            type="button"
            className="btn-secundario"
            disabled={ocupado}
            onClick={() => {
              const motivo = window.prompt('Por que reabrir o mês? (fica registrado)');
              if (motivo === null) return;
              executar(() => reabrirMes(clinicaId, mes, motivo));
            }}
          >
            Reabrir o mês
          </button>
        ) : oferta.mostrar ? (
          <button
            type="button"
            className="btn-primario"
            disabled={ocupado || linhas.length === 0}
            onClick={() => {
              const texto = textoDaConfirmacao({
                mes,
                total: totais.total,
                lancamentos: lancamentosDoMes(recebimentos, mes),
                ultimaImportacao,
              });
              if (!window.confirm(texto)) return;
              executar(() => fecharMes(clinicaId, mes));
            }}
          >
            {ocupado ? 'Fechando…' : 'Fechar o mês'}
          </button>
        ) : (
          // ★★ Itens 2 e 6 — sem botão no mês que não terminou e na folha de R$ 0,00, com o porquê.
          <span className="campo-ajuda">{oferta.motivo}</span>
        )}
      </footer>
    </section>
  );
}
